import { inject, Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { ScannerService } from '../../services/connectors/scanner.service';
import { CompanyService } from '../services/company.service';

/**
 * ConnectorConfigGuard
 * Tier-aware guard that allows route activation based on user tier and connector configuration.
 *
 * **Tier-based Access Logic:**
 * - **FREE tier**: Users don't need scanner connectors (demo data mode). Always allow access.
 * - **TRIAL/PREMIUM tier**: Users need a configured scanner connector for real data access.
 *
 * **BYOS (Bring Your Own Scanner) Mode:**
 * - Allows users with existing scanner credentials to configure local-first scanning
 * - Credentials stored securely in IndexedDB with encryption
 *
 * **IMPORTANT LIMITATION:**
 * This guard checks if ScannerService.connector is set, which is an in-memory property
 * that is lost on page refresh. Even if credentials are stored in IndexedDB, a page
 * refresh will cause this guard to redirect TRIAL/PREMIUM users to setup.
 *
 * **Future Enhancement:**
 * The connector should be automatically restored from persistent storage on app
 * initialization (e.g., via APP_INITIALIZER or similar mechanism) before routing occurs.
 */
@Injectable({ providedIn: 'root' })
export class ConnectorConfigGuard implements CanActivate {
  private scanner = inject(ScannerService);
  private router = inject(Router);
  private companyService = inject(CompanyService);

  canActivate(): boolean | UrlTree {
    // Get current company to check tier
    const company = this.companyService.company;

    // If company not loaded yet, allow through (other guards will handle auth)
    // This prevents blocking during app initialization
    if (!company) {
      return true;
    }

    // FREE tier users don't need connectors - they use demo data
    // Allow them to proceed without connector setup
    if (company.tier === 'free') {
      return true;
    }

    // TRIAL and PREMIUM users ideally have a configured connector for real data
    // access, but users should be able to skip setup and manage scanners later
    // from the dashboard. Allow activation regardless of connector state.
    // The dashboard already surfaces links and prompts to configure scanners.
    return true;
  }
}
